import type { Locale } from '@iris/i18n';
import { useLocale } from '@iris/i18n/react';
import { type ApiClient, type AppKind, type AuthResponse, type Me, unwrap } from '@iris/shared';
import { type AuthBridge } from '@iris/ui/lib/auth-helpers';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * loading: restoring the session from the refresh cookie at boot · anonymous: no session · authenticated ·
 * offline: the server could not be reached at boot (the app keeps showing cached data, T4 §4.6) and tries again
 * when the device is back online.
 */
export type AuthStatus = 'loading' | 'anonymous' | 'authenticated' | 'offline';

interface AuthState {
  status: AuthStatus;
  user: Me | null;
  /** the user chose to sign out (no "return to" page after the next login) */
  signedOut: boolean;
}

export interface AuthContextValue extends AuthState {
  app: AppKind;
  api: ApiClient['api'];
  /** After login or sign-up: keeps the access token in memory and the user in state. */
  /**
   * `next`: where to go instead of the return URL (sign-up → /welcome). The PublicOnly guard does the
   * redirect, so it never races with a lazy route still loading.
   */
  signIn(response: AuthResponse, next?: string): void;
  /** Read by PublicOnly; cleared once a protected screen is shown. */
  nextAfterSignIn: string | null;
  clearNextAfterSignIn(): void;
  setUser(user: Me): void;
  /** POST /auth/logout (or logout-all), then forgets the session locally. */
  signOut(options?: { everywhere?: boolean }): Promise<void>;
  /** Forgets the session locally only (after a password reset revoked every session). */
  forgetSession(): void;
  /** US-C20: switches the language at once and saves it to the profile when logged in. */
  changeLocale(locale: Locale): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const ANONYMOUS: AuthState = { status: 'anonymous', user: null, signedOut: false };

/**
 * Session state of one app (FR-AUTH-06, -08): the access token lives in the API client's memory, the refresh
 * token in this app's httpOnly cookie. At boot the cookie is turned into a session; the client reports later
 * refreshes and lost sessions through `bridge`. `onSignedOut` lets the app drop its cached data.
 */
export function AuthProvider({
  app,
  client,
  bridge,
  onSignedOut,
  children,
}: {
  app: AppKind;
  client: ApiClient;
  bridge: AuthBridge;
  onSignedOut?: () => void;
  children: ReactNode;
}) {
  const { locale, setLocale } = useLocale();
  const [state, setState] = useState<AuthState>({ status: 'loading', user: null, signedOut: false });
  const sessionUserId = useRef<string | null>(null);
  const nextAfterSignIn = useRef<string | null>(null);
  const localeRef = useRef(locale);
  localeRef.current = locale;
  const onSignedOutRef = useRef(onSignedOut);
  onSignedOutRef.current = onSignedOut;

  const applyUser = useCallback(
    (user: Me) => {
      // The profile language wins when a session starts (US-C20: the preference is saved to the profile).
      if (sessionUserId.current !== user.id) {
        sessionUserId.current = user.id;
        if (user.locale !== localeRef.current) setLocale(user.locale);
      }
      setState({ status: 'authenticated', user, signedOut: false });
    },
    [setLocale],
  );

  const forget = useCallback(
    (signedOut: boolean) => {
      client.session.setAccessToken(null);
      sessionUserId.current = null;
      nextAfterSignIn.current = null;
      setState({ ...ANONYMOUS, signedOut });
      onSignedOutRef.current?.();
    },
    [client],
  );

  useEffect(
    () =>
      bridge.subscribe((event) => {
        if (event.type === 'session') applyUser(event.session.user);
        else forget(false);
      }),
    [bridge, applyUser, forget],
  );

  useEffect(() => {
    let active = true;
    const restore = () => {
      client.session.refresh().then(
        (ok) => {
          if (active && !ok) setState((s) => (s.status === 'authenticated' ? s : ANONYMOUS));
        },
        () => {
          if (active) setState((s) => (s.status === 'authenticated' ? s : { ...s, status: 'offline' }));
        },
      );
    };
    restore();
    window.addEventListener('online', restore);
    return () => {
      active = false;
      window.removeEventListener('online', restore);
    };
  }, [client]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      app,
      api: client.api,
      nextAfterSignIn: nextAfterSignIn.current,
      clearNextAfterSignIn: () => {
        nextAfterSignIn.current = null;
      },
      signIn: (response, next) => {
        nextAfterSignIn.current = next ?? null;
        client.session.setAccessToken(response.accessToken);
        applyUser(response.user);
      },
      setUser: applyUser,
      signOut: async ({ everywhere = false } = {}) => {
        try {
          await client.api.POST(everywhere ? '/api/v1/auth/logout-all' : '/api/v1/auth/logout');
        } catch {
          // offline: the session is forgotten here anyway; the server one expires on its own
        }
        forget(true);
      },
      forgetSession: () => forget(true),
      changeLocale: (next) => {
        setLocale(next);
        if (state.status !== 'authenticated') return;
        client.api
          .PATCH('/api/v1/me', { body: { locale: next } })
          .then(unwrap)
          .then(applyUser)
          .catch(() => {
            // the device keeps the choice; the profile is updated next time
          });
      },
    }),
    [state, app, client, applyUser, forget, setLocale],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}
